import { useMemo } from "react";
import { Navigate, useParams } from "react-router-dom";

import { useHighchartsTimezoneDefaults } from "@/lib/timezone/use-highcharts-timezone-defaults";
import Timezone from "@/lib/timezone/Timezone";
import { WidgetGrid } from "@/features/dashboard/components";
import { usePublishedPageQuery } from "@/features/dashboard/features/dashboard";
import { getNavigationElementBySlug } from "@/features/shared/navigation";
import { useDashboardData, useDashboardWarningSettings } from "@/features/shared/hooks";
import { usePublishedDashboardsQuery } from "@/features/shared/hooks/queries";
import { WidgetRuntimeProvider } from "@/features/shared/widget-context/widget-runtime-context";
import Toolbar from "@/features/dashboard/components/toolbar/toolbar";
import TimeRange from "@/types/time-range";
import WarningPage from "@/features/dashboard/features/general/pages/warning-page";
import WarningsOverviewPage from "@/features/dashboard/features/general/pages/warnings-overview";
import {
  NAVIGATION_ELEMENT_KIND,
  navigationElementKind,
} from "@/lib/navigation-element-kinds";
import { hasMaritimeWarningsEnabled } from "@/lib/maritime-warning-page-settings.utils";

/** Built-in warning pages have navigation slugs, but no CMS page_id. */
const DashboardPage = () => {
  const { page, subPage } = useParams();
  const { navigationElements } = useDashboardData();
  const currentPage = getNavigationElementBySlug(navigationElements ?? [], page, subPage);
  const kind = navigationElementKind(currentPage);
  if (kind === NAVIGATION_ELEMENT_KIND.publicWarningsPage) return <WarningPage />;
  if (kind === NAVIGATION_ELEMENT_KIND.maritimeWarningsPage) {
    return <WarningsPageRoute />;
  }
  return <CmsDashboardPage />;
};

/**
 * Maritime warnings page. Same rule as the nav item: maritime switch on with at least one
 * domain. Waits for the dashboards so a direct load does not redirect on the defaults.
 */
export const WarningsPageRoute = () => {
  const { dashboard } = useParams();
  const dashboardsQuery = usePublishedDashboardsQuery();
  const { maritimeWarningPage } = useDashboardWarningSettings();
  if (dashboardsQuery.isLoading) return null;
  return hasMaritimeWarningsEnabled(maritimeWarningPage) ? (
    <WarningsOverviewPage />
  ) : (
    <Navigate to={`/${dashboard ?? ""}`} replace />
  );
};

const CmsDashboardPage = () => {
  const { page, subPage } = useParams();
  const { selectedDashboard: dashboard, navigationElements } = useDashboardData();
  const currentPage = getNavigationElementBySlug(navigationElements ?? [], page, subPage);
  const pageId = currentPage?.page_id;
  const pageQuery = usePublishedPageQuery(String(dashboard?.id), String(pageId));

  // Seed the runtime context from the dashboard's configured default range;
  // re-seeds automatically on dashboard change (see WidgetRuntimeProvider).
  const initialTimeRange = useMemo(() => {
    const settings = dashboard?.time_range_selector_settings;
    if (!settings) return undefined;
    return new TimeRange(settings.default_start, settings.default_end);
  }, [dashboard?.time_range_selector_settings]);

  const sortedContainers = useMemo(() => {
    if (!pageQuery.data) return [];

    return pageQuery.data.widget_containers.sort((a, b) => {
      if (a.position_x < b.position_x) {
        return -1;
      } else if (a.position_x > b.position_x) {
        return 1;
      }

      if (a.position_y < b.position_y) {
        return -1;
      } else if (a.position_y > b.position_y) {
        return 1;
      }

      return 0;
    });
  }, [pageQuery.data]);

  const [hasLocationSelector, hasTimeRangeSelector] = useMemo(() => {
    if (!pageQuery.data) return [false, false];

    const selectorsPerWidget = pageQuery.data.widget_containers.flatMap((item) =>
      item.widgets.map((widget) => {
        const { location, time_range } = widget.configuration?.inputs ?? {};
        return [!!location, !!time_range];
      }),
    );

    return selectorsPerWidget.reduce(
      (acc, curr) => {
        acc[0] ||= curr[0];
        acc[1] ||= curr[1];
        return acc;
      },
      [false, false],
    );
  }, [pageQuery.data]);

  const timezone = useMemo(
    () => Timezone.fromSettings(dashboard?.time_zone_settings),
    [dashboard?.time_zone_settings],
  );

  // Global Highcharts defaults (timezone + weekday names) for every widget chart
  // on this page. Applied once here rather than per widget.
  useHighchartsTimezoneDefaults(timezone);

  if (!pageQuery.data || !dashboard) return null;
  // TODO: handle loading state
  if (pageQuery.isLoading) return <div>Loading...</div>;

  return (
    <WidgetRuntimeProvider
      initialTimeRange={initialTimeRange}
      timezone={timezone}
      autoRefreshInterval={1 * 60 * 1000}
    >
      <Toolbar
        dashboardId={dashboard.id}
        showLocationSelector={hasLocationSelector}
        showTimeRangeSelector={hasTimeRangeSelector}
      />
      <WidgetGrid containers={sortedContainers ?? []} />
    </WidgetRuntimeProvider>
  );
};

export default DashboardPage;
